import {DeepPartial, voidOp} from '@practicegenius/pg-util/src/TypescriptUtils';
import {Logger} from '@practicegenius/domain/src/common/logger';
import {ApiClient, ApiError} from '@practicegenius/http-clients-js/src/types';
import {makeNilApiClient} from '@practicegenius/http-clients-js/src/ApiClient';
import {NavigationEventSchema} from '@practicegenius/domain/src/machines/events';
import {initialLogger, NilNavigator} from '../data/placeholders';
import {
  AlreadyRegisteredHint,
  AlreadySocialRegisteredHint,
  RegistrationData,
  SocialPlatform,
} from '../data/types';
import {Navigator} from '../data/navigator';
import {routes} from '../routes';

type ValidationErrors = DeepPartial<
  Record<keyof RegistrationData, string> & {
    account: Record<keyof RegistrationData['account'], string>;
  }
>;

export type ValidationErrorCodes =
  | 'generic'
  | 'account_registered'
  | 'social_registered';

export enum State {
  boot = 'boot',
  determineBootRequirements = 'determineBootRequirements',
  bootRegistrationProcessFromToken = 'bootRegistrationProcessFromToken',
  bootRegistrationProcessWithSkippedRequirements = 'bootRegistrationProcessWithSkippedRequirements',

  nextRequirementScreen = 'nextRequirementScreen',
  nextPreMethodSelectionRequirementScreen = 'nextPreMethodSelectionRequirementScreen',
  routeFromPath = 'routeFromPath',

  idle = 'idle',

  linkPractice = 'linkPractice',
  connectingRewardsCard = 'connectingRewardsCard',
  connectingBusinessCode = 'connectingBusinessCode',
  handleSocialRegistrationOrProceed = 'handleSocialRegistrationOrProceed',
  bootSocialRegisteredProcessFromToken = 'bootSocialRegisteredProcessFromToken',

  tellUsMore = 'tellUsMore',
  submittingLeadSourceInfo = 'submittingLeadSourceInfo',

  registrationMethods = 'registrationMethods',
  updateRegistrationViaSocial = 'updateRegistrationViaSocial',
  bootRegistrationProcessFromUnregisteredInput = 'bootRegistrationProcessFromUnregisteredInput',
  bootRegistrationProcessFromUnregisteredCardNumber = 'bootRegistrationProcessFromUnregisteredCardNumber',
  bootRegistrationProcessFromUnregisteredBusinessCode = 'bootRegistrationProcessFromUnregisteredBusinessCode',
  bootRegistrationProcessFromInviteCode = 'bootRegistrationProcessFromInviteCode',

  noGenericRegistration = 'noGenericRegistration',

  nameVerification = 'nameVerification',
  verifyingName = 'verifyingName',
  committingRewardsCard = 'committingRewardsCard',
  nameMismatch = 'nameMismatch',

  birthdate = 'birthdate',
  submittingBirthdate = 'submittingBirthdate',

  phoneNumber = 'phoneNumber',
  submittingPhoneNumber = 'submittingPhoneNumber',

  email = 'email',
  submittingEmail = 'submittingEmail',

  emailVerification = 'emailVerification',
  submittingVerificationCode = 'submittingVerificationCode',
  resendingVerificationEmail = 'resendingVerificationEmail',

  emailVerified = 'emailVerified',
  resumingRegistration = 'resumingRegistration',
  checkingRegistrationStatus = 'checkingRegistrationStatus',

  username = 'username',
  submittingUsername = 'submittingUsername',

  password = 'password',
  submittingPassword = 'submittingPassword',

  success = 'success',
  loggingInViaOauthTokens = 'loggingInViaOauthTokens',

  processingInviteCode = 'processingInviteCode',
  processingBusinessCode = 'processingBusinessCode',
  alreadyRegistered = 'alreadyRegistered',
  alreadyRegisteredSocially = 'alreadyRegisteredSocially',
}

export type StateSchema = {
  states: {
    boot: {
      states: {
        determineBootRequirements: {};
        bootRegistrationProcessFromToken: {};
        bootRegistrationProcessWithSkippedRequirements: {};
      };
    };
    nextRequirementScreen: {};
    nextPreMethodSelectionRequirementScreen: {};
    routeFromPath: {};

    linkPractice: {
      states: {
        idle: {};
        connectingRewardsCard: {};
        connectingBusinessCode: {};
        handleSocialRegistrationOrProceed: {};
        bootSocialRegisteredProcessFromToken: {};
      };
    };

    tellUsMore: {
      states: {
        idle: {};
        submittingLeadSourceInfo: {};
      };
    };

    processingInviteCode: {};
    processingBusinessCode: {};

    registrationMethods: {
      states: {
        boot: {};
        idle: {};
        updateRegistrationViaSocial: {};
        bootRegistrationProcessFromUnregisteredInput: {};
        bootRegistrationProcessFromUnregisteredCardNumber: {};
        bootRegistrationProcessFromUnregisteredBusinessCode: {};
      };
    };

    noGenericRegistration: {};

    nameVerification: {
      states: {
        idle: {};
        verifyingName: {};
      };
    };

    nameMismatch: {};

    birthdate: {
      states: {
        idle: {};
        submittingBirthdate: {};
      };
    };

    phoneNumber: {
      states: {
        idle: {};
        submittingPhoneNumber: {};
      };
    };

    email: {
      states: {
        idle: {};
        submittingEmail: {};
      };
    };

    emailVerification: {
      states: {
        idle: {};
        submittingVerificationCode: {};
        resendingVerificationEmail: {};
      };
    };

    emailVerified: {
      states: {
        boot: {};
        idle: {};
        resumingRegistration: {};
        checkingRegistrationStatus: {};
      };
    };

    username: {
      states: {
        idle: {};
        submittingUsername: {};
      };
    };

    password: {
      states: {
        idle: {};
        submittingPassword: {};
      };
    };

    success: {
      states: {
        idle: {};
        loggingInViaOauthTokens: {};
      };
    };

    alreadyRegistered: {};
    alreadyRegisteredSocially: {};
  };
};

export type Context = RegistrationData & {
  // This will have to be addressed when we move this to the mobile app, we need to know what kind of navigator we're using
  logger: Logger;
  navigator: Navigator;
  apiClient: ApiClient;
  sitesDotClient: ApiClient;
  validationErrors: ValidationErrors;
  showAlert: (msg: string) => void;
  redirect: {
    toLogin: () => void;
    toLoginAsUser: (user: string) => void;
    toSocialLoginWithProvider: (provider: string) => void;
    // This is optional because it doesn't make sense in the mobile context
    toAnywhere?: (path: string) => void;
  };
  registrationMethod?: RegistrationMethod;
};

export const defaultContext: Context = {
  logger: initialLogger,
  navigator: new NilNavigator(),
  apiClient: makeNilApiClient(),
  sitesDotClient: makeNilApiClient(),
  showAlert: voidOp,
  redirect: {
    toLogin: voidOp,
    toLoginAsUser: voidOp,
    toSocialLoginWithProvider: voidOp,
  },

  account: {
    id: null,
    firstname: null,
    middlename: null,
    lastname: null,
    birthdate: null,
    username: null,
    email: null,
    responsibleEmail: null,
    useResponsibleEmail: null,
    password: null,
    contactId: null,
    phone: null,
    responsiblePhone: null,
    useResponsiblePhone: null,
    relationship: null,
    why: null,
    questionsOrComments: null,
  },

  practiceUserRecord: {
    id: -1,
    practiceId: -1,
    firstname: null,
    lastname: null,
    birthdate: null,
    email: null,
    responsibleEmail: null,
    phone: null,
    responsiblePhone: null,
    country: null,
  },

  allowSocialRegistration: true,
  rememberMe: false,
  cardNumber: null,
  inviteCode: null,
  businessCode: null,
  linkedPractice: null,
  linkedViaSocial: false,
  socialProvider: null,
  token: null,
  remainingRequirements: [],
  registrationCompleted: false,
  authorization: null,
  emailVerificationCode: '',
  confirmedEmail: {email: '', responsibleEmail: ''},

  validationErrors: {},
};

export enum Event {
  NEXT = 'NEXT',
  BACK = 'BACK',
  RESTART = 'RESTART',
  RESTART_WITH_SKIP = 'RESTART_WITH_SKIP',

  UPDATE_DATA = 'UPDATE_DATA',
  HAS_VALIDATION_ERRORS = 'HAS_VALIDATION_ERRORS',
  UPDATE_REGISTRATION = 'UPDATE_REGISTRATION',
  UPDATE_REGISTRATION_METHOD = 'UPDATE_REGISTRATION_METHOD',

  CONNECT_REWARDS_CARD = 'CONNECT_REWARDS_CARD',
  CONNECT_BUSINESS_CODE = 'CONNECT_BUSINESS_CODE',
  GO_TO_GENERIC_REGISTRATION_WARNING = 'GO_TO_GENERIC_REGISTRATION_WARNING',

  LOGIN_VIA_SOCIAL = 'LOGIN_VIA_SOCIAL',

  RETRY_NAME_VERIFICATION = 'RETRY_NAME_VERIFICATION',
  NAME_NOT_MATCHED = 'NAME_NOT_MATCHED',

  SUBMIT_EMAIL = 'SUBMIT_EMAIL',
  SKIP_TO_EMAIL_CONFIRMED = 'SKIP_TO_EMAIL_CONFIRMED',

  SUBMIT_VERIFICATION_CODE = 'SUBMIT_VERIFICATION_CODE',
  MARK_EMAIL_VERIFIED = 'MARK_EMAIL_VERIFIED',
  RESEND_VERIFICATION_EMAIL = 'RESEND_VERIFICATION_EMAIL',

  MAYBE_RESUME_REGISTRATION = 'MAYBE_RESUME_REGISTRATION',
  CHECK_REGISTRATION_STATUS = 'CHECK_REGISTRATION_STATUS',

  SUBMIT_USERNAME = 'SUBMIT_USERNAME',

  SUBMIT_PASSWORD = 'SUBMIT_PASSWORD',

  GO_TO_ACCOUNT = 'GO_TO_ACCOUNT',
  LOGIN_VIA_OAUTH_TOKENS = 'LOGIN_VIA_OAUTH_TOKENS',

  REGISTRATION_NOT_FOUND = 'REGISTRATION_NOT_FOUND',

  SKIP_TO_SUCCESS = 'SKIP_TO_SUCCESS',
  SKIP_TO_ALREADY_REGISTERED = 'SKIP_TO_ALREADY_REGISTERED',
  SKIP_TO_SOCIAL_ALREADY_REGISTERED = 'SKIP_TO_SOCIAL_ALREADY_REGISTERED',
  SKIP_TO_EMAIL = 'SKIP_TO_EMAIL',
  GO_TO_LOGIN = 'GO_TO_LOGIN',

  UNHANDLED_ERROR = 'UNHANDLED_ERROR',
}

export type RegistrationMethod =
  | {
      type: 'email';
    }
  | {
      type: 'social';
      provider: SocialPlatform;
    };

export type EventSchema =
  | NavigationEventSchema
  | {
      type: Event.NEXT;
      payload: {completed: boolean; requires: string[]; draftAccount: any};
    }
  | {type: Event.BACK}
  | {
      type: Event.RESTART;
    }
  | {
      type: Event.RESTART_WITH_SKIP;
      businessCode: string;
      skipTo: typeof routes.TellUsMore | typeof routes.RegistrationMethods;
    }
  | {type: Event.UPDATE_DATA; payload: DeepPartial<RegistrationData>}
  | {type: Event.HAS_VALIDATION_ERRORS; errors: ValidationErrors}
  | {
      type: Event.UPDATE_REGISTRATION;
      payload?:
        | Partial<RegistrationData['account']>
        | DeepPartial<RegistrationData>;
      include?: string[];
    }
  | {
      type: Event.UPDATE_REGISTRATION_METHOD;
      payload: RegistrationMethod;
    }
  | {type: Event.CONNECT_REWARDS_CARD}
  | {type: Event.CONNECT_BUSINESS_CODE; businessCode: string}
  | {type: Event.GO_TO_GENERIC_REGISTRATION_WARNING}
  | {type: Event.LOGIN_VIA_SOCIAL; socialProvider: SocialPlatform}
  | {type: Event.NAME_NOT_MATCHED}
  | {type: Event.RETRY_NAME_VERIFICATION}
  | {type: Event.SUBMIT_EMAIL}
  | {type: Event.SUBMIT_VERIFICATION_CODE}
  | {type: Event.MARK_EMAIL_VERIFIED; field: 'email' | 'responsibleEmail'}
  | {type: Event.RESEND_VERIFICATION_EMAIL}
  | {type: Event.MAYBE_RESUME_REGISTRATION; include: ['practices']}
  | {type: Event.CHECK_REGISTRATION_STATUS}
  | {type: Event.SUBMIT_USERNAME}
  | {type: Event.SUBMIT_PASSWORD}
  | {type: Event.GO_TO_ACCOUNT}
  | {type: Event.LOGIN_VIA_OAUTH_TOKENS}
  | {type: Event.REGISTRATION_NOT_FOUND}
  | {type: Event.SKIP_TO_EMAIL_CONFIRMED}
  | {type: Event.SKIP_TO_SUCCESS}
  | {
      type: Event.SKIP_TO_ALREADY_REGISTERED;
      message: string;
      hint: AlreadyRegisteredHint;
    }
  | {
      type: Event.SKIP_TO_SOCIAL_ALREADY_REGISTERED;
      message: string;
      hint: AlreadySocialRegisteredHint;
    }
  | {type: Event.SKIP_TO_EMAIL}
  | {type: Event.GO_TO_LOGIN; hint: AlreadyRegisteredHint}
  | {type: Event.UNHANDLED_ERROR; message?: string; error: Error | ApiError};
